import {
    AfterContentChecked,
    ChangeDetectorRef,
    Directive,
    ElementRef,
    HostBinding,
    Input,
    OnDestroy
} from "@angular/core";
import {AbstractControl, FormControl, FormGroup} from "@angular/forms";
import {DataResourceModel, EntityData, ValidationMessage, ValidationService} from "@fedeltapointofsale/data-client";
import {
    FUIAlertActions,
    FUIAlertService,
    FUIFormationService,
    FUISpinnersService,
    FUIStackedPageContextService
} from "@fedeltapointofsale/ui";
import {EntityFormControl} from "./edit/entity-form-builder/entity-form-control";
import {EntityFormBuilder} from "./edit/entity-form-builder/entity-form-builder";
import {EditService} from "./edit/edit-page/edit.service";
import {EntityFormCollectionControl} from "./edit/entity-form-builder/entity-form-collection-control";
import {environment} from "../../../environments/environment";
import {Config} from "../../config";
import {ErrorHandlerService} from "../../error-handler.service";
import {concatAll, concatMap, EMPTY, find, from, Observable, of, reduce, Subscription} from "rxjs";
import {TranslateService} from "@ngx-translate/core";

/**
 * Abstract class that provide an entity data and data validation. Also provides functions to deal with Stacked pages.
 */
@Directive()
export abstract class AbstractDataPageComponent implements AfterContentChecked, OnDestroy {
    LOCALISATION = Config.LOCALISATION;

    @Input("title") title: string;

    @HostBinding("style.display") private display: string = "flex";
    @HostBinding("style.width") private width: string = "100%";

    private _formGroup: FormGroup;

    protected subscriptions: Subscription = new Subscription();

    titleLabel: string;

    get formGroup(): FormGroup {
        return this._formGroup;
    }

    @Input()
    set formGroup(value: FormGroup) {
        this._formGroup = value;
        if (this.editService) {
            this.editService.formGroup = value;
        }
    }

    constructor(public dataResourceModel: DataResourceModel<any>,
                public validationService: ValidationService,
                public editService: EditService,
                public changeDetectorRef: ChangeDetectorRef,
                public entityFormBuilder: EntityFormBuilder,
                public formationService: FUIFormationService,
                public alertService: FUIAlertService,
                public translate: TranslateService,
                public spinnersService: FUISpinnersService,
                public errorHandlerService: ErrorHandlerService,
                public stackedPageContextService: FUIStackedPageContextService,
                public application: string,
                public elementRef: ElementRef) {

        this.formGroup = this.entityFormBuilder.group(this.dataResourceModel);

        this.formationService.formGroupValidation = this.formGroup;

        this.formationService.formSubmitted = this.editService.submitted;

        this.formationService.formGroupResolver = function (formGroup: FormGroup): AbstractControl[] {
            const controls: AbstractControl[] = [];
            for (const key in formGroup.controls) {
                if (formGroup.controls[key] instanceof FormControl && !(formGroup.controls[key] instanceof EntityFormCollectionControl)) {
                    controls.push(formGroup.controls[key]);
                }
            }
            return controls;
        };

        if (this.stackedPageContextService) {
            this.subscriptions.add(this.stackedPageContextService.onCloseRequest.subscribe((closeConfirmation) => {
                closeConfirmation.canClose = this.closeStackedPage();
            }));

            setTimeout(() => this.stackedPageContextService.title = this.titleLabel);
        }
    }

    public markControlsAsUntouchedAndPristine(formGroup: FormGroup): void {
        for (const key in formGroup.controls) {
            if (formGroup.controls[key] instanceof FormGroup) {
                this.markControlsAsUntouchedAndPristine(<FormGroup>formGroup.controls[key]);
            } else {
                this.markControlAsUntouchedAndPristine(formGroup, key);
            }
        }
        formGroup.markAsPristine();
    }

    public markControlAsUntouchedAndPristine(formGroup: FormGroup, key: string) {
        formGroup.controls[key].markAsUntouched({onlySelf: true});
        formGroup.controls[key].markAsPristine({onlySelf: true});
        formGroup.controls[key].updateValueAndValidity({onlySelf: true, emitEvent: true});
    }

    /**
     * Performs necessary actions to indicate validation messages to the user.
     * Messages are displayed asynchronously.
     *
     * @param validationMessages
     */
    protected handleValidationMessages(validationMessages: ValidationMessage[]): void {
        if (validationMessages) {
            let firstGroupToShow: number = Number.MAX_SAFE_INTEGER;
            validationMessages.forEach((validationMessage) => {
                const index: number = this.formationService.listGroups.findIndex((group) => {
                    return group.formControls.find((control) => (<EntityFormControl>control).path === validationMessage.fieldPath) != undefined;
                });

                if (index >= 0) {
                    firstGroupToShow = Math.min(firstGroupToShow, index);
                }
            });

            if (firstGroupToShow !== Number.MAX_SAFE_INTEGER) {
                this.formationService.setActiveGroup(this.formationService.listGroups[firstGroupToShow]);
            }

            this.getTranslatedValidationMessages(validationMessages, this.dataResourceModel.data)
                .subscribe((combinedMessage: string) => {
                    this.alertService.showError(undefined, "'" + combinedMessage + "'").subscribe(() => {
                    });
                });
        } else {
            this.errorHandlerService.displayError("wbo.common.error.resolveErrors");
        }
    }

    ngAfterContentChecked(): void {
        // when a formation group is loaded and it is using formGroup it is throwing an exception ExpressionChangedAfterItHasBeenCheckedError so
        // detecting the changes here is solving the issue
        if (!environment.production) {
            this.changeDetectorRef.detectChanges();
        }
    }

    public isFormValid(): boolean {
        return this.editService.isFormValid();
    }

    public isFormPending(): boolean {
        return this.editService.isFormPending();
    }

    /**
     * Returns a Observable containing the translated validation messages.
     * @param validationMessages
     * @param entityData
     * @returns {Observable<string>} Observable that resolves to a string containing the validation messages concatenated
     */
    private getTranslatedValidationMessages(validationMessages: ValidationMessage[], entityData: EntityData<any>): Observable<string> {
        return of(
            this.translate.get("wbo.common.error.formErrors"),
            from(validationMessages).pipe(
                concatMap((validationMessage: ValidationMessage) =>
                    of(
                        validationMessage.fieldPath
                            ? this.translate.get(this.application + "." + this.dataResourceModel.getResource() + ".edit." + "field." + validationMessage.fieldPath + ".label")
                            : EMPTY,
                        this.translate.get(validationMessage.messageKey, validationMessage.messageParams)
                    ).pipe(
                        concatAll(),
                        reduce((messageLine: string, translation: string) => messageLine ? messageLine + " - " + translation : translation, "")
                    )
                )
            )
        ).pipe(
            concatAll(),
            reduce((combinedMessages: string, messageLine: string) => combinedMessages + messageLine + "<br><br>", "")
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    closeStackedPage(): Promise<boolean> {
        if (this.editService.dirty && this.shouldCheckDiscardChanges()) {
            return new Promise((resolve, reject) => {
                this.alertService.showConfirmationOkCancel("wbo.edit.prompt.discard.heading", "wbo.edit.prompt.discard.message").subscribe((action) => {
                    resolve(action === FUIAlertActions.OK_ACTION);
                });
            });
        } else {
            return Promise.resolve(true);
        }
    }

    shouldCheckDiscardChanges(): boolean {
        return true;
    }

    performAction(): void {
        this.editService.$openGridEdits.pipe(
            find(openGridEdits => openGridEdits === 0)
        ).subscribe(() => {
            this.editService.confirmSubmission();

            const confirmation: Promise<Boolean> | boolean = this.beforeExecution();
            if (confirmation instanceof Promise) {
                confirmation.then((confirmed: Boolean) => {
                    if (confirmed) {
                        this.performConfirmedAction();
                    }
                });
            } else if (confirmation) {
                this.performConfirmedAction();
            }
        });
    }

    async performConfirmedAction(): Promise<void> {
        return this.performActionIfValid(async () => {
            try {
                const confirmed: Boolean = await this.execute();
                if (confirmed) {
                    this.editService.dirty = false;
                }
            } catch (error) {
                this.errorHandlerService.displayDataClientError(error);
            }
        });
    }

    async performActionIfValid(action: () => Promise<void>): Promise<void> {
        this.spinnersService.open();
        const valid: Boolean = await this.validate();
        if (valid) {
            await action();
        }
        this.spinnersService.close();
    }

    async validate(): Promise<Boolean> {
        while (this.isFormPending()) {
            await new Promise<void>((resolve) => setTimeout(() => resolve(), 100));
        }
        try {
            const validationMessages: ValidationMessage[] = await this.validationService.validateEntity(this.dataResourceModel.data);
            if (this.isFormValid() && (validationMessages == null || validationMessages.length === 0)) {
                return true;
            } else {
                this.handleValidationMessages(validationMessages);

                // mark all controls as touched to show the validations
                this.entityFormBuilder.markControlsAsTouched(this.formGroup);

                this.scrollToFieldInError();
            }
        } catch (error) {
            this.errorHandlerService.displayDataClientError(error);
        }
        return false;
    }

    scrollToFieldInError(): void {
        if (this.isFormPending()) {
            setTimeout(() => this.scrollToFieldInError(), 100);
        } else {
            const elements: HTMLCollection = this.elementRef.nativeElement.getElementsByTagName("fui-error-validation");
            for (let n = 0; n < elements.length; n++) {
                const element = elements.item(n);
                if (element.classList.contains("valid")) {
                    this.scrollToElement(element);
                    return;
                }
            }
            const elementsGrid: HTMLCollection = this.elementRef.nativeElement.getElementsByTagName("wbo-edit-collection-validation");
            for (let n = 0; n < elementsGrid.length; n++) {
                const elementGrid = elementsGrid.item(n);
                if (elementGrid.getElementsByClassName("error-markup").length) {
                    this.scrollToElement(elementGrid);
                    return;
                }
            }
        }
    }

    private getScrollablePageElement() {
        let canvas = this.elementRef.nativeElement.getElementsByTagName("fui-page-pane")[0];
        if (canvas.scrollHeight <= canvas.clientHeight) {
            canvas = this.elementRef.nativeElement.getElementsByTagName("fui-page-subject")[0];
        }
        return canvas;
    }

    protected scrollToElement(element): void {
        const canvas = this.getScrollablePageElement();
        const pageHeader = document.getElementsByTagName("fui-page-header")[0];
        const computedStyle = window.getComputedStyle(pageHeader);
        const offset: number = computedStyle.position === "sticky" ? 116 : 0;
        canvas.scrollTop = canvas.scrollTop + (element.getBoundingClientRect().top - canvas.getBoundingClientRect().top) - (offset + 40);
    }

    protected scrollToTop(): void {
        this.getScrollablePageElement().scrollTop = 0;
    }

    protected beforeExecution(): Promise<Boolean> | boolean {
        return true;
    }

    protected abstract execute(): Promise<Boolean>;
}
